import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { chatActionService } from "../../services/api";

const LeaveGroupModal = ({
  isOpen,
  onClose,
  pitchId,
  cid,
  channelId,
  channel,
  currentAccountId,
  onLeaveSuccess,
  isChannelAdmin
}) => {
  // Pitch channels keep the pitch endpoints; direct_* channels leave by cid.
  const usePitch = Boolean(pitchId);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [error, setError] = useState(null);
  const [selectedNewAdminId, setSelectedNewAdminId] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setStaffOptions([]);
      setError(null);
      setSelectedNewAdminId(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || (!pitchId && !cid)) {
      return;
    }

    let cancelled = false;

    const fetchStaff = async () => {


      if(!isChannelAdmin)  return
      setIsLoading(true);
      setError(null);

      try {
        const response = usePitch
          ? await chatActionService.getPitchStaff(pitchId)
          : await chatActionService.getChannelStaff(cid);

        if (cancelled) {
          return;
        }

        if (!response.success) {
          throw new Error(response.error || "Failed to load staff");
        }

        setStaffOptions(response.results || []);
      } catch (err) {
        console.error("LeaveGroupModal: fetch error", err);
        if (!cancelled) {
          setError(err.message || "Failed to load staff");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchStaff();

    return () => {
      cancelled = true;
    };
  }, [isOpen, pitchId, cid, usePitch, isChannelAdmin]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isSubmitting) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, isSubmitting, onClose]);

  const channelMembers = useMemo(
    () => staffOptions.filter((staff) => Boolean(staff.already_in_channel)),
    [staffOptions]
  );

  const isCurrentUserAdmin = isChannelAdmin

  const eligibleSuccessors = useMemo(() => {
    return channelMembers.filter(
      (staff) =>
        staff.account_id != currentAccountId && Boolean(staff.can_contact_agents)
    );
  }, [channelMembers, currentAccountId]);

  const memberCount = useMemo(() => channelMembers.length, [channelMembers]);

  const selectedSuccessor = useMemo(
    () =>
      eligibleSuccessors.find(
        (staff) => String(staff.account_id) === String(selectedNewAdminId),
      ) || null,
    [eligibleSuccessors, selectedNewAdminId],
  );
  const successorName =
    selectedSuccessor?.name ||
    selectedSuccessor?.email ||
    (selectedSuccessor ? `Account ${selectedSuccessor.account_id}` : "");
  const successorFirstName = String(successorName).trim().split(/\s+/)[0] || "";

  const cannotLeaveDueToMissingSuccessor = isCurrentUserAdmin && eligibleSuccessors.length === 0;

  useEffect(() => {
    if (
      isOpen &&
      isCurrentUserAdmin &&
      eligibleSuccessors.length &&
      !selectedNewAdminId
    ) {
      setSelectedNewAdminId(eligibleSuccessors[0].account_id);
    }
  }, [isOpen, isCurrentUserAdmin, eligibleSuccessors, selectedNewAdminId]);

  const handleSubmit = async () => {
    if (!pitchId && !cid) {
      toast.error("Missing channel information");
      return;
    }

    if (cannotLeaveDueToMissingSuccessor) {
      toast.error("Assign another eligible staff member before leaving");
      return;
    }

    if (isCurrentUserAdmin && !selectedNewAdminId) {
      toast.error("Select a new chat admin before leaving");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = usePitch
        ? await chatActionService.leavePitchChannel(pitchId, {
            channel_id: channelId,
            ...(isCurrentUserAdmin
              ? { new_admin_account_id: selectedNewAdminId }
              : {}),
          })
        : await chatActionService.leaveChannel(
            cid,
            isCurrentUserAdmin ? selectedNewAdminId : undefined,
          );

      if (!response.success) {
        throw new Error(response.error || "Failed to leave chat");
      }

      toast.success("You have left the chat");

      try {
        await channel?.hide?.();
      } catch (hideError) {
        console.warn("LeaveGroupModal: failed to hide channel", hideError);
      }

      onLeaveSuccess?.(response);
      onClose();
    } catch (err) {
      console.error("LeaveGroupModal: submit error", err);
      toast.error(err.message || "Failed to leave chat");
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={isSubmitting ? undefined : onClose}
        aria-hidden="true"
      />

      {/* #34: bottom sheet under md, centered dialog from md up. */}
      <div className="relative bg-white rounded-t-2xl md:rounded-2xl shadow-xl w-full md:max-w-xl md:mx-4 max-h-[90vh] overflow-y-auto">
        <div className="md:hidden flex justify-center pt-2.5 pb-1">
          <span className="h-1 w-10 rounded-full bg-[#E6EAF2]" />
        </div>
        <div className="px-6 pt-6 pb-4 border-b border-[#E5E7EB]">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-[#111927]">
                Leave this conversation
              </h3>
              <p className="text-sm text-[#6C737F] mt-1">
                {isCurrentUserAdmin
                  ? "You are the admin. Before you go, choose who takes over."
                  : "You will no longer receive updates from this conversation."}
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="text-[#6C737F] hover:text-[#111927] transition-colors disabled:opacity-50"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="mt-3 text-xs text-[#6C737F]">
            Current members: {memberCount || "Unknown"}
          </div>
        </div>

        <div className="px-6 py-5">
          {isLoading ? (
            <div className="py-10 text-center text-sm text-[#6C737F]">
              Loading participants...
            </div>
          ) : error ? (
            <div className="py-10 text-center text-sm text-red-600">{error}</div>
          ) : isCurrentUserAdmin ? (
            <div className="space-y-4">
              <p className="text-sm text-[#111927]">
                Select a staff member who will take over as the chat admin:
              </p>
              {eligibleSuccessors.length ? (
                <ul className="space-y-3 max-h-64 overflow-y-auto pr-1">
                  {eligibleSuccessors.map((staff) => (
                    <li key={staff.account_id} className="border border-[#E5E7EB] rounded-xl px-4 py-3 flex items-start gap-3">
                      <label className="flex items-start gap-3 cursor-pointer w-full">
                        <input
                          type="radio"
                          name="new-admin"
                          className="mt-1"
                          value={staff.account_id}
                          checked={selectedNewAdminId === staff.account_id}
                          onChange={() => setSelectedNewAdminId(staff.account_id)}
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-[#111927]">
                            {staff.name || staff.email || `Account ${staff.account_id}`}
                          </div>
                          <div className="text-xs text-[#6C737F] mt-1">
                            {staff.email || "Email not available"}
                          </div>
                          <div className="mt-2 inline-flex px-2 py-1 text-xs bg-[#ECFDF3] text-[#027A48] rounded-full">
                            Can contact agents
                          </div>
                        </div>
                      </label>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="border border-[#FEE4E2] bg-[#FEF3F2] rounded-xl px-4 py-3 text-sm text-[#B42318]">
                  No eligible staff members available. Please ensure another staff member has agent contact permissions before you leave.
                </div>
              )}
              {selectedSuccessor && (
                // 5c: succession and timer transfer in one breath, inside the
                // confirm — admin role and timer rights travel together.
                <div className="bg-[#FFF9F0] border border-[#F3DDC4] rounded-[10px] px-3 py-2.5 text-xs text-[#344054] leading-relaxed">
                  <span className="font-bold">
                    {successorFirstName} becomes admin
                  </span>{" "}
                  of this conversation.{" "}
                  <span className="font-bold">Timer control passes to them.</span>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4 text-sm text-[#111927]">
              <p>
                You will be removed from this conversation and will no longer have access to its history. You can rejoin if another admin invites you again.
              </p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#E5E7EB] bg-[#F9FAFB] rounded-b-2xl flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-[#111927] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F4F4F4] transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              isLoading ||
              !!error ||
              (isCurrentUserAdmin && (!selectedNewAdminId || cannotLeaveDueToMissingSuccessor))
            }
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 ${
              isCurrentUserAdmin
                ? "bg-[#101828] hover:bg-black"
                : "bg-[#F04438] hover:bg-[#D92D20]"
            }`}
          >
            {isSubmitting && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {isCurrentUserAdmin
              ? successorFirstName
                ? `Leave & make ${successorFirstName} admin`
                : "Leave & assign admin"
              : "Leave conversation"}
          </button>
        </div>
      </div>
    </div>
  );

  if (!isOpen) {
    return null;
  }

  return createPortal(modalContent, document.body);
};

export default LeaveGroupModal;
